import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, FileText, UserCheck, Settings, CheckCircle2 } from 'lucide-react';
import { onboardingService } from '@/services/onboardingService';
import { profileApi } from '@/services/profileApi';
import { fetchIdentityProfile, getLocalUserInfo, setLocalUserInfo, toDateOnly } from '@/services/authApi';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { isLocalMode } from '@/utils/localMode';
import type { AuthenticationProviderName, UserOnboardingRequest } from '@/types';

interface OnboardingConsentPageProps {
  sessionId: string;
  /** Called when completion succeeds; pass user email so parent can redirect to learning path. */
  onComplete: (userEmail: string) => void;
}

const COMPLETE_REQUEST_STORAGE_KEY = 'five_ai_onboarding_complete_request';
const AUTH_PROVIDER_STORAGE_KEY = 'five_ai_onboarding_auth_provider';
const PLAN_CODE_STORAGE_KEY = 'five_ai_onboarding_plan_code';
const SELECTED_PLAN_DATA_KEY = 'five_ai_onboarding_selected_plan_data';

export const OnboardingConsentPage: React.FC<OnboardingConsentPageProps> = ({
  sessionId,
  onComplete,
}) => {
  const isLocalModeEnabled = useMemo(() => isLocalMode(), []);

  // User info required by /complete
  const [signUpEmail, setSignUpEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [whatsAppSameAsPhone, setWhatsAppSameAsPhone] = useState(true);
  const [whatsAppCountryCode, setWhatsAppCountryCode] = useState('+1');
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [authProvider, setAuthProvider] = useState<AuthenticationProviderName>('LOCAL');

  const [formData, setFormData] = useState({
    // Terms Acceptance (default checked so user can complete with one click)
    termsAccepted: true,
    termsVersion: '1.0',
    
    // User Consent (default checked)
    privacyPolicyAccepted: true,
    marketingConsent: true,
    dataProcessingConsent: true,
    thirdPartyDataSharingConsent: true,
    
    // GDPR Compliance
    rightToBeInformed: true,
    rightToAccess: true,
    rightToRectification: true,
    rightToErasure: true,
    rightToDataPortability: true,
    gdprVersion: '1.0',
    
    // Preferences
    preferredLanguage: navigator.language || 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    inAppNotifications: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stored = getLocalUserInfo();
    if (stored.signUpEmail ?? stored.email) setSignUpEmail((stored.signUpEmail ?? stored.email) ?? '');
    if (stored.firstName) setFirstName(stored.firstName);
    if (stored.lastName) setLastName(stored.lastName);
    if (stored.phoneCountryCode) setPhoneCountryCode(stored.phoneCountryCode);
    else setPhoneCountryCode('+1');
    if (stored.phoneNumber) setPhoneNumber(stored.phoneNumber);
    if (stored.dateOfBirth) setDateOfBirth(toDateOnly(stored.dateOfBirth));
    if (stored.gender) setGender(stored.gender);

    try {
      const storedProvider = localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) as AuthenticationProviderName | null;
      if (storedProvider) setAuthProvider(storedProvider);
    } catch {
      // ignore
    }
  }, []);

  // Fallback: if name/email are still missing after reading localStorage, fetch from identity service.
  // This covers cases where the OIDC enrichment in OAuthCallback didn't run (direct navigation,
  // network failure, or any OAuth provider that didn't return claims in the JWT).
  useEffect(() => {
    const accessToken = localStorage.getItem('five_ai_access_token');
    if (!accessToken) return;

    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchIdentityProfile(accessToken);
        if (cancelled) return;

        // Only fill fields that are currently empty — never overwrite user input
        if (profile.email)     setSignUpEmail(prev => prev || profile.email!);
        if (profile.firstName) setFirstName(prev => prev || profile.firstName!);
        if (profile.lastName)  setLastName(prev => prev || profile.lastName!);
        if (profile.phoneNumber?.number) {
          setPhoneNumber(prev => prev || profile.phoneNumber!.number!);
          if (profile.phoneNumber?.countryCode) {
            setPhoneCountryCode(prev => prev || profile.phoneNumber!.countryCode!);
          }
        }
        if (profile.timezone) {
          setFormData(prev => ({ ...prev, timezone: prev.timezone || profile.timezone! }));
        }

        // Persist back to auth store via auth service so subsequent renders are pre-filled
        const existing = getLocalUserInfo();
        setLocalUserInfo({
          signUpEmail: existing.signUpEmail || profile.email || '',
          email: existing.email || profile.email || '',
          firstName: existing.firstName || profile.firstName || '',
          lastName: existing.lastName || profile.lastName || '',
          ...(profile.phoneNumber?.countryCode && { phoneCountryCode: profile.phoneNumber.countryCode }),
          ...(profile.phoneNumber?.number && { phoneNumber: profile.phoneNumber.number }),
          ...(profile.dateOfBirth && { dateOfBirth: profile.dateOfBirth }),
          ...(profile.gender && { gender: profile.gender }),
        });
      } catch {
        // Identity service unavailable — form fields stay empty, user fills them in
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setLocalUserInfo({
      signUpEmail,
      email: signUpEmail,
      firstName,
      lastName,
      phoneCountryCode,
      phoneNumber,
      dateOfBirth,
      gender,
    });
  }, [signUpEmail, firstName, lastName, phoneCountryCode, phoneNumber, dateOfBirth, gender]);

  const handleCheckboxChange = (field: string, value: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleComplete = async () => {
    // Validate required fields
    if (!formData.termsAccepted || !formData.privacyPolicyAccepted || !formData.dataProcessingConsent) {
      setError('Please accept the required terms and consents to continue.');
      return;
    }

    // Validate required user fields for /complete
    if (!firstName?.trim() || !lastName?.trim() || !signUpEmail?.trim() || !phoneNumber?.trim()) {
      setError('Please provide your first name, last name, email, and phone number to continue.');
      return;
    }
    if (!phoneCountryCode?.trim()) {
      setError('Please provide a valid phone country code.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Check if email already exists before finalizing
      try {
        const existingProfile = await profileApi.getById(signUpEmail);
        if (existingProfile && existingProfile.userId) {
          setError(`An account with email ${signUpEmail} already exists. Please use a different email or sign in.`);
          setIsSubmitting(false);
          return;
        }
      } catch (err: unknown) {
        if ((err as { status?: number })?.status !== 404) {
          console.error('[OnboardingConsent] Error checking existing profile:', err);
        }
      }

      // All datetime fields use ISO 8601 (e.g. 2026-01-22T08:34:00.154Z) to match working complete API.
      const timestamp = new Date().toISOString();
      const ipAddress = '1.1.1.1'; // placeholder; backend captures real IP from headers

      // Ensure subscription + consents are also reflected in their respective PATCH endpoints.
      // (Some backends/read models rely on these fields being updated outside /complete.)
      const storedPlanCode =
        typeof window !== 'undefined' ? window.localStorage.getItem(PLAN_CODE_STORAGE_KEY) : null;
      // Read stored plan data for subscriptionRequest
      let storedPlanData: {
        planCode?: string;
        billingEmail?: string;
        billingCycle?: string;
        currency?: string;
        countryCode?: string;
        paymentGateway?: string;
      } | null = null;
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SELECTED_PLAN_DATA_KEY) : null;
        if (raw) {
          storedPlanData = JSON.parse(raw);
          console.log('[OnboardingConsent] Retrieved stored plan data:', storedPlanData);
        }
      } catch (err) {
        console.warn('[OnboardingConsent] Failed to parse stored plan data:', err);
      }
      // CRITICAL: PATCH calls MUST complete BEFORE calling /complete to ensure data is persisted
      // Call them sequentially to ensure proper order and error handling
      const termsAcceptancePayload = {
        termsVersion: formData.termsVersion,
        acceptedAt: timestamp,
        acceptedIpAddress: ipAddress,
        acceptedUserAgent: navigator.userAgent,
      };
      
      // Ensure all userConsent fields are explicitly set (no undefined values)
      // This is critical for consentData to be populated by the backend
      const userConsentPayload = {
        termsAccepted: Boolean(formData.termsAccepted),
        privacyPolicyAccepted: Boolean(formData.privacyPolicyAccepted),
        marketingEmails: Boolean(formData.marketingConsent),
        smsNotifications: Boolean(formData.smsNotifications),
        pushNotifications: Boolean(formData.pushNotifications),
        dataAnalytics: Boolean(formData.dataProcessingConsent),
        thirdPartySharing: Boolean(formData.thirdPartyDataSharingConsent),
        cookieConsent: true,
        consentTimestamp: timestamp,
        consentVersion: '1.0',
        consentIpAddress: ipAddress,
      };
      
      console.log('[OnboardingConsent] userConsentPayload prepared:', JSON.stringify(userConsentPayload, null, 2));
      
      const gdprCompliancePayload = {
        dataProcessingConsent: formData.dataProcessingConsent,
        consentGivenAt: timestamp,
        legalBasis: 'CONSENT',
      };
      
      const preferencesPayload = {
        preferredLanguage: formData.preferredLanguage,
        timezone: formData.timezone,
        additionalPreferences: {
          notificationPreferences: {
            email: formData.emailNotifications,
            push: formData.pushNotifications,
            sms: formData.smsNotifications,
            inApp: formData.inAppNotifications,
          },
        },
      };
      
      console.log('[OnboardingConsent] Starting PATCH calls BEFORE /complete...');
      
      // Call PATCH endpoints sequentially to ensure they complete before /complete
      const patchResults: Array<{ endpoint: string; status: 'fulfilled' | 'rejected'; result?: unknown; error?: unknown }> = [];
      
      // 1. Update subscription
      try {
        console.log('[OnboardingConsent] PATCH /subscription...');
        await onboardingService.updateSubscription(sessionId, {
          planCode: storedPlanCode || undefined,
          billingEmail: signUpEmail,
        });
        console.log('[OnboardingConsent] ✅ PATCH /subscription succeeded');
        patchResults.push({ endpoint: 'subscription', status: 'fulfilled' });
      } catch (err: unknown) {
        const error = err as { message?: string };
        console.warn('[OnboardingConsent] ⚠️ PATCH /subscription failed:', error?.message || err);
        patchResults.push({ endpoint: 'subscription', status: 'rejected', error: err });
      }
      
      // 2. Accept terms (CRITICAL - must complete before /complete)
      try {
        console.log('[OnboardingConsent] PATCH /terms-acceptance...', termsAcceptancePayload);
        await onboardingService.acceptTerms(sessionId, termsAcceptancePayload);
        console.log('[OnboardingConsent] ✅ PATCH /terms-acceptance succeeded');
        patchResults.push({ endpoint: 'terms-acceptance', status: 'fulfilled' });
      } catch (err: unknown) {
        const error = err as { message?: string };
        console.error('[OnboardingConsent] ❌ PATCH /terms-acceptance FAILED:', error?.message || err);
        patchResults.push({ endpoint: 'terms-acceptance', status: 'rejected', error: err });
        // In local mode, continue even if this fails
        if (!isLocalModeEnabled) {
          throw new Error(`Failed to update terms acceptance: ${error?.message || String(err)}`);
        }
      }
      
      // 3. Submit user consent (CRITICAL - must complete before /complete)
      // This should populate consentData in the backend
      try {
        console.log('[OnboardingConsent] PATCH /user-consent...', JSON.stringify(userConsentPayload, null, 2));
        const userConsentResponse = await onboardingService.submitUserConsent(sessionId, userConsentPayload);
        console.log('[OnboardingConsent] ✅ PATCH /user-consent succeeded', userConsentResponse);
        patchResults.push({ endpoint: 'user-consent', status: 'fulfilled', result: userConsentResponse });
      } catch (err: unknown) {
        console.error('[OnboardingConsent] ❌ PATCH /user-consent FAILED:', (err as Error)?.message || err);
        console.error('[OnboardingConsent] Full error:', err);
        patchResults.push({ endpoint: 'user-consent', status: 'rejected', error: err });
        if (!isLocalModeEnabled) {
          throw new Error(`Failed to update user consent: ${(err as Error)?.message ?? String(err)}`);
        }
      }
      
      // 4. Submit GDPR compliance (CRITICAL - must complete before /complete)
      try {
        console.log('[OnboardingConsent] PATCH /gdpr-compliance...', gdprCompliancePayload);
        await onboardingService.submitGDPRCompliance(sessionId, gdprCompliancePayload);
        console.log('[OnboardingConsent] ✅ PATCH /gdpr-compliance succeeded');
        patchResults.push({ endpoint: 'gdpr-compliance', status: 'fulfilled' });
      } catch (err: unknown) {
        console.error('[OnboardingConsent] ❌ PATCH /gdpr-compliance FAILED:', (err as Error)?.message || err);
        patchResults.push({ endpoint: 'gdpr-compliance', status: 'rejected', error: err });
        if (!isLocalModeEnabled) {
          throw new Error(`Failed to update GDPR compliance: ${(err as Error)?.message ?? String(err)}`);
        }
      }
      
      // 5. Update preferences (CRITICAL - must complete before /complete)
      try {
        console.log('[OnboardingConsent] PATCH /preferences...', preferencesPayload);
        await onboardingService.updatePreferences(sessionId, preferencesPayload);
        console.log('[OnboardingConsent] ✅ PATCH /preferences succeeded');
        patchResults.push({ endpoint: 'preferences', status: 'fulfilled' });
      } catch (err: unknown) {
        console.error('[OnboardingConsent] ❌ PATCH /preferences FAILED:', (err as Error)?.message || err);
        patchResults.push({ endpoint: 'preferences', status: 'rejected', error: err });
        if (!isLocalModeEnabled) {
          throw new Error(`Failed to update preferences: ${(err as Error)?.message ?? String(err)}`);
        }
      }
      
      // 6. Update consent-data (NEW - populates consentData field)
      const consentDataPayload = {
        termsAccepted: Boolean(formData.termsAccepted),
        termsAcceptedAt: timestamp,
        privacyPolicyAccepted: Boolean(formData.privacyPolicyAccepted),
        privacyPolicyAcceptedAt: timestamp,
        marketingConsent: Boolean(formData.marketingConsent),
        marketingConsentAt: timestamp,
        dataProcessingConsent: Boolean(formData.dataProcessingConsent),
        dataProcessingConsentAt: timestamp,
        analyticsConsent: Boolean(formData.dataProcessingConsent), // Map dataProcessingConsent to analyticsConsent
        analyticsConsentAt: timestamp,
        termsVersion: formData.termsVersion,
        privacyPolicyVersion: formData.termsVersion, // Use same version for privacy policy
      };
      
      try {
        console.log('[OnboardingConsent] PATCH /consent-data...', JSON.stringify(consentDataPayload, null, 2));
        await onboardingService.updateConsentData(sessionId, consentDataPayload);
        console.log('[OnboardingConsent] ✅ PATCH /consent-data succeeded');
        patchResults.push({ endpoint: 'consent-data', status: 'fulfilled' });
      } catch (err: unknown) {
        const error = err as { message?: string };
        console.error('[OnboardingConsent] ❌ PATCH /consent-data FAILED:', error?.message || err);
        patchResults.push({ endpoint: 'consent-data', status: 'rejected', error: err });
        if (!isLocalModeEnabled) {
          throw new Error(`Failed to update consent data: ${error?.message || String(err)}`);
        }
      }
      
      const rejected = patchResults.filter((r) => r.status === 'rejected');
      const fulfilled = patchResults.filter((r) => r.status === 'fulfilled');
      
      console.log('[OnboardingConsent] PATCH calls summary:', {
        total: patchResults.length,
        succeeded: fulfilled.length,
        failed: rejected.length,
        results: patchResults.map((r) => ({ endpoint: r.endpoint, status: r.status })),
      });
      
      if (rejected.length > 0) {
        console.warn('[OnboardingConsent] Some PATCH endpoints failed, but continuing to /complete:', {
          failedEndpoints: rejected.map((r) => r.endpoint),
          errors: rejected.map((r) => ((r.error as { message?: string })?.message) || String(r.error)),
        });
      }
      
      // Small delay to ensure backend has processed all PATCH calls and computed consentData
      // consentData is likely computed by the backend from userConsent, termsAcceptance, and gdprCompliance
      console.log('[OnboardingConsent] Waiting for backend to process PATCH calls and compute consentData...');
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Fetch /details AFTER PATCH calls to verify data was persisted
      // This ensures we can see if the PATCH calls successfully updated the session
      // consentData should be computed by backend from userConsent, termsAcceptance, and gdprCompliance
      let sessionDetails: Record<string, unknown> | null = null;
      try {
        console.log('[OnboardingConsent] Fetching /details to verify PATCH calls persisted data...');
        sessionDetails = (await onboardingService.getSessionDetails(sessionId)) as unknown as Record<string, unknown>;
        console.log('[OnboardingConsent] /details response:', {
          hasTermsAcceptance: !!sessionDetails?.termsAcceptance,
          hasGdprCompliance: !!sessionDetails?.gdprCompliance,
          hasUserConsent: !!sessionDetails?.userConsent,
          hasConsentData: !!sessionDetails?.consentData,
          hasPreferences: !!sessionDetails?.preferences,
          termsAcceptance: sessionDetails?.termsAcceptance,
          gdprCompliance: sessionDetails?.gdprCompliance,
          userConsent: sessionDetails?.userConsent,
          consentData: sessionDetails?.consentData,
          preferences: sessionDetails?.preferences,
        });
        
        if (!sessionDetails?.consentData) {
          console.warn('[OnboardingConsent] ⚠️ consentData is still null after PATCH calls. Backend may compute it later or it may require all consent fields to be set.');
        } else {
          console.log('[OnboardingConsent] ✅ consentData is populated:', sessionDetails.consentData);
        }
      } catch (detailsError) {
        console.warn('[OnboardingConsent] Failed to load session details (non-fatal):', detailsError);
      }

      // Set userName to email ID as requested
      const finalUserName = signUpEmail.trim();

      // Ensure firstName and lastName are trimmed (validation above ensures they're not empty)
      const finalFirstName = firstName.trim();
      const finalLastName = lastName.trim();

      // userId is NOT part of the request - it's returned by the Identity Service from the complete API.
      // Including it at root level causes 403.
      const completePayload: UserOnboardingRequest = {
        firstName: finalFirstName,
        lastName: finalLastName,
        email: signUpEmail,
        phoneNumber: {
          type: 'MOBILE',
          countryCode: phoneCountryCode?.trim() || '+1',
          number: phoneNumber?.trim() || '',
        },
        whatsAppSameAsPhone,
        whatsAppNumber: whatsAppSameAsPhone ? undefined : {
          type: 'MOBILE' as const,
          countryCode: whatsAppCountryCode?.trim() || '+1',
          number: whatsAppNumber?.trim() || '',
        },
        dateOfBirth: dateOfBirth?.trim() ? dateOfBirth.trim().split('T')[0] : undefined,
        gender: (gender?.trim() as 'MALE' | 'FEMALE' | 'NON_BINARY' | 'PREFER_NOT_TO_SAY' | 'OTHER') || undefined,
        userName: finalUserName,
        authenticationProvider: {
          provider: authProvider,
          authenticated: true,
        },
        preferences: {
          preferredLanguage: formData.preferredLanguage || 'en',
          timezone: formData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          additionalPreferences: {
            notificationPreferences: {
              email: formData.emailNotifications,
              push: formData.pushNotifications,
              sms: formData.smsNotifications,
              inApp: formData.inAppNotifications,
            },
          },
        },
        gdprCompliance: {
          dataProcessingConsent: formData.dataProcessingConsent,
          consentGivenAt: timestamp,
          legalBasis: 'CONSENT',
        },
        termsAcceptance: {
          termsVersion: formData.termsVersion,
          acceptedAt: timestamp,
          acceptedIpAddress: ipAddress,
          acceptedUserAgent: navigator.userAgent,
        },
        userConsent: {
          termsAccepted: formData.termsAccepted,
          privacyPolicyAccepted: formData.privacyPolicyAccepted,
          marketingEmails: formData.marketingConsent,
          smsNotifications: formData.smsNotifications,
          pushNotifications: formData.pushNotifications,
          dataAnalytics: formData.dataProcessingConsent,
          thirdPartySharing: formData.thirdPartyDataSharingConsent,
          cookieConsent: true,
          consentTimestamp: timestamp,
          consentVersion: '1.0',
          consentIpAddress: ipAddress,
        },
      };

      console.log('[OnboardingConsent] Complete payload before merge:', {
        firstName: completePayload.firstName,
        lastName: completePayload.lastName,
        userName: completePayload.userName,
        email: completePayload.email,
        phoneNumber: completePayload.phoneNumber,
        preferences: completePayload.preferences,
        termsAcceptance: completePayload.termsAcceptance,
        termsAcceptanceType: typeof completePayload.termsAcceptance,
        termsAcceptanceKeys: completePayload.termsAcceptance ? Object.keys(completePayload.termsAcceptance) : 'null/undefined',
      });

      // Merge optional details into payload if not present.
      // NOTE: /complete schema supports location, deviceInfo, surveyResponses, subscriptionRequest.
      // CRITICAL: Do NOT merge or overwrite user-provided fields: termsAcceptance, gdprCompliance, userConsent, preferences, phoneNumber, userName, firstName, lastName, email
      // These MUST come from the form/user input, not from sessionDetails.
      if (sessionDetails) {
        console.log('[OnboardingConsent] sessionDetails received:', {
          hasTermsAcceptance: !!sessionDetails.termsAcceptance,
          hasGdprCompliance: !!sessionDetails.gdprCompliance,
          hasUserConsent: !!sessionDetails.userConsent,
          hasPreferences: !!sessionDetails.preferences,
          hasPhoneNumber: !!sessionDetails.phoneNumber,
          hasUserName: !!sessionDetails.userName,
          keys: Object.keys(sessionDetails),
        });
        
        const detailsDeviceInfo = sessionDetails.deviceInfo;
        const detailsLocation = sessionDetails.locationInfo;
        const detailsSurveyResponses = sessionDetails.surveyResponses;
        const detailsSubscriptionRequest = sessionDetails.subscriptionRequest;

        // Only merge optional fields that don't conflict with user-provided data.
        // Shape merged data to match working complete API JSON.
        if (!completePayload.deviceInfo && detailsDeviceInfo && typeof detailsDeviceInfo === 'object') {
          const raw = detailsDeviceInfo as Record<string, unknown>;
          const toIsoZ = (v: unknown): string => {
            if (v == null) return new Date().toISOString();
            const d = typeof v === 'string' ? new Date(v) : v instanceof Date ? v : null;
            return d && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();
          };
          completePayload.deviceInfo = {
            ...raw,
            firstSeenAt: toIsoZ(raw.firstSeenAt),
            lastSeenAt: toIsoZ(raw.lastSeenAt),
          };
        }
        if (!completePayload.location && detailsLocation && typeof detailsLocation === 'object') {
          const loc = detailsLocation as Record<string, unknown>;
          completePayload.location = {
            latitude: loc.latitude ?? null,
            longitude: loc.longitude ?? null,
            country: loc.country ?? null,
            state: loc.state ?? null,
            city: loc.city ?? null,
            postalCode: loc.postalCode ?? null,
            timezone: loc.timezone ?? null,
          };
        }
        if (!completePayload.surveyResponses && Array.isArray(detailsSurveyResponses)) {
          completePayload.surveyResponses = (detailsSurveyResponses as Array<Record<string, unknown>>).map((r) => ({
            surveyId: r.surveyId,
            questionId: r.questionId,
            questionText: r.questionText,
            answer: r.answer,
            answerType: r.answerType,
            metadata: r.metadata ?? null,
          }));
        }
        // Only include subscriptionRequest if it exists and is an object (avoid sending incomplete CreateSubscriptionRequest).
        if (
          !completePayload.subscriptionRequest &&
          detailsSubscriptionRequest &&
          typeof detailsSubscriptionRequest === 'object'
        ) {
          completePayload.subscriptionRequest = detailsSubscriptionRequest as Record<string, unknown>;
        }
        
        // WARN if sessionDetails tries to overwrite user-provided fields
        if (sessionDetails.termsAcceptance) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains termsAcceptance - IGNORING (using user-provided value)');
        }
        if (sessionDetails.gdprCompliance) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains gdprCompliance - IGNORING (using user-provided value)');
        }
        if (sessionDetails.userConsent) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains userConsent - IGNORING (using user-provided value)');
        }
        if (sessionDetails.preferences) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains preferences - IGNORING (using user-provided value)');
        }
        if (sessionDetails.phoneNumber) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains phoneNumber - IGNORING (using user-provided value)');
        }
        if (sessionDetails.userName) {
          console.warn('[OnboardingConsent] ⚠️ sessionDetails contains userName - IGNORING (using user-provided value)');
        }
      }

      // Populate subscriptionRequest from stored plan data
      // billingCycle and currency are MANDATORY fields from the plan
      if (storedPlanData) {
        // Validate mandatory fields
        if (!storedPlanData.billingCycle) {
          console.error('[OnboardingConsent] ERROR: billingCycle is missing from stored plan data!', storedPlanData);
        }
        if (!storedPlanData.currency) {
          console.error('[OnboardingConsent] ERROR: currency is missing from stored plan data!', storedPlanData);
        }
        
        if (!completePayload.subscriptionRequest) {
          // Create new subscriptionRequest from stored plan data
          // billingCycle and currency are MANDATORY - must be included from plan
          // userId is NOT in subscriptionRequest; it's returned by Identity Service from /complete.
          const subscriptionRequest: Record<string, unknown> = {
            planCode: storedPlanData.planCode,
            billingEmail: storedPlanData.billingEmail || signUpEmail,
            billingCycle: storedPlanData.billingCycle || 'MONTHLY',
            currency: storedPlanData.currency || 'USD',
          };
          if (storedPlanData.countryCode) subscriptionRequest.countryCode = storedPlanData.countryCode;
          if (storedPlanData.paymentGateway) subscriptionRequest.paymentGateway = storedPlanData.paymentGateway;
          completePayload.subscriptionRequest = subscriptionRequest;
          console.log('[OnboardingConsent] Populated subscriptionRequest from stored plan data:', completePayload.subscriptionRequest);
        } else {
          // Merge missing fields from stored plan data into existing subscriptionRequest
          const existing = completePayload.subscriptionRequest as Record<string, unknown>;
          const subscriptionRequest: Record<string, unknown> = {
            planCode: existing.planCode ?? storedPlanData.planCode ?? undefined,
            billingEmail: existing.billingEmail ?? storedPlanData.billingEmail ?? signUpEmail,
            billingCycle: existing.billingCycle ?? storedPlanData.billingCycle ?? 'MONTHLY',
            currency: existing.currency ?? storedPlanData.currency ?? 'USD',
          };
          if (existing.countryCode || storedPlanData.countryCode) subscriptionRequest.countryCode = existing.countryCode ?? storedPlanData.countryCode;
          if (existing.paymentGateway || storedPlanData.paymentGateway) subscriptionRequest.paymentGateway = existing.paymentGateway ?? storedPlanData.paymentGateway;
          completePayload.subscriptionRequest = subscriptionRequest;
          console.log('[OnboardingConsent] Merged stored plan data into existing subscriptionRequest:', completePayload.subscriptionRequest);
        }
      } else {
        if (completePayload.subscriptionRequest) {
          const existing = completePayload.subscriptionRequest as Record<string, unknown>;
          completePayload.subscriptionRequest = {
            ...existing,
            billingCycle: existing.billingCycle ?? 'MONTHLY',
            currency: existing.currency ?? 'USD',
          };
          console.warn('[OnboardingConsent] No stored plan data found, using existing subscriptionRequest with fallbacks:', completePayload.subscriptionRequest);
        }
      }

      // Final validation: Ensure all required fields are present and not null/undefined
      if (!completePayload.termsAcceptance || typeof completePayload.termsAcceptance !== 'object') {
        console.error('[OnboardingConsent] ERROR: termsAcceptance is missing or invalid! Creating fallback...');
        completePayload.termsAcceptance = {
          termsVersion: formData.termsVersion,
          acceptedAt: timestamp,
          acceptedIpAddress: ipAddress,
          acceptedUserAgent: navigator.userAgent,
        };
      }
      
      if (!completePayload.gdprCompliance || typeof completePayload.gdprCompliance !== 'object') {
        console.error('[OnboardingConsent] ERROR: gdprCompliance is missing or invalid! Creating fallback...');
        completePayload.gdprCompliance = {
          dataProcessingConsent: formData.dataProcessingConsent,
          consentGivenAt: timestamp,
          legalBasis: 'CONSENT',
        };
      }
      
      if (!completePayload.userConsent || typeof completePayload.userConsent !== 'object') {
        console.error('[OnboardingConsent] ERROR: userConsent is missing or invalid! Creating fallback...');
        completePayload.userConsent = {
          termsAccepted: formData.termsAccepted,
          privacyPolicyAccepted: formData.privacyPolicyAccepted,
          marketingEmails: formData.marketingConsent,
          smsNotifications: formData.smsNotifications,
          pushNotifications: formData.pushNotifications,
          dataAnalytics: formData.dataProcessingConsent,
          thirdPartySharing: formData.thirdPartyDataSharingConsent,
          cookieConsent: true,
          consentTimestamp: timestamp,
          consentVersion: '1.0',
          consentIpAddress: ipAddress,
        };
      }
      
      if (!completePayload.preferences || typeof completePayload.preferences !== 'object') {
        console.error('[OnboardingConsent] ERROR: preferences is missing or invalid! Creating fallback...');
        completePayload.preferences = {
          preferredLanguage: formData.preferredLanguage || 'en',
          timezone: formData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          additionalPreferences: {
            notificationPreferences: {
              email: formData.emailNotifications,
              push: formData.pushNotifications,
              sms: formData.smsNotifications,
              inApp: formData.inAppNotifications,
            },
          },
        };
      }
      
      if (!completePayload.phoneNumber || typeof completePayload.phoneNumber !== 'object') {
        console.error('[OnboardingConsent] ERROR: phoneNumber is missing or invalid! Creating fallback...');
        completePayload.phoneNumber = {
          type: 'MOBILE',
          countryCode: phoneCountryCode?.trim() || '+1',
          number: phoneNumber?.trim() || '',
        };
      }
      
      if (!completePayload.userName || typeof completePayload.userName !== 'string') {
        console.error('[OnboardingConsent] ERROR: userName is missing or invalid! Setting to email...');
        completePayload.userName = signUpEmail.trim();
      }

      // Ensure subscriptionRequest never contains userId (working complete API shape; Identity Service returns it).
      if (completePayload.subscriptionRequest && typeof completePayload.subscriptionRequest === 'object') {
        const sr = completePayload.subscriptionRequest as Record<string, unknown>;
        delete sr.userId;
      }

      // userId is NOT sent at root level - it's returned by the Identity Service from the complete API.
      const finalPayload = completePayload;
      
      console.log('[OnboardingConsent] Final complete payload being sent (FULL):', JSON.stringify(finalPayload, null, 2));
      console.log('[OnboardingConsent] Final complete payload summary:', {
        firstName: finalPayload.firstName,
        lastName: finalPayload.lastName,
        userName: finalPayload.userName,
        email: finalPayload.email,
        phoneNumber: finalPayload.phoneNumber,
        phoneNumberValid: !!(finalPayload.phoneNumber?.countryCode && finalPayload.phoneNumber?.number),
        preferences: finalPayload.preferences,
        preferencesValid: !!(finalPayload.preferences?.preferredLanguage && finalPayload.preferences?.timezone),
        gdprCompliance: finalPayload.gdprCompliance,
        gdprComplianceValid: !!(finalPayload.gdprCompliance?.dataProcessingConsent !== undefined),
        termsAcceptance: finalPayload.termsAcceptance,
        termsAcceptanceValid: !!(finalPayload.termsAcceptance?.termsVersion && finalPayload.termsAcceptance?.acceptedAt),
        userConsent: finalPayload.userConsent,
        userConsentValid: !!(finalPayload.userConsent?.termsAccepted !== undefined),
        subscriptionRequest: finalPayload.subscriptionRequest,
      });

      try {
        localStorage.setItem(COMPLETE_REQUEST_STORAGE_KEY, JSON.stringify(finalPayload));
      } catch {
        // ignore
      }

      // Fire /complete in the background — it can take ~1 min (bio creation, etc.)
      console.log('[OnboardingConsent] Calling /complete endpoint (runs in background; may take ~1 min)...');
      onboardingService.complete(sessionId, finalPayload).then(
        () => console.log('[OnboardingConsent] ✅ /complete endpoint finished (profile/bio/learning path ready).'),
        (err: unknown) => {
          const e = err as { status?: number; message?: string };
          if (isLocalModeEnabled && e?.status === 403) {
            console.warn('[OnboardingConsent] Local mode: /complete returned 403.', e);
          } else {
            console.error('[OnboardingConsent] /complete failed in background:', err);
          }
        }
      );

      const email = finalPayload.email?.trim() || signUpEmail?.trim() || '';
      onComplete(email || '');
    } catch (err: unknown) {
      const error = err as { message?: string };
      console.error('[OnboardingConsent] Failed to complete onboarding:', err);
      setError(error?.message || 'Failed to save your preferences. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const allRequiredAccepted = formData.termsAccepted && formData.privacyPolicyAccepted && formData.dataProcessingConsent;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24 px-4 sm:px-6">
        <div className="container-custom max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-4 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
            Almost There!
          </h1>
          <p className="text-lg text-muted-foreground">
            Review and accept our terms, privacy policy, and set your preferences
          </p>
        </div>

        {error && (
          <Card className="border-destructive/50 bg-destructive/10 animate-fade-in">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* User details (required to submit /complete) */}
        <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <UserCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">Your Details</CardTitle>
            </div>
            <CardDescription>
              Confirm your information before accepting terms and completing setup.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="consent-firstname">First Name <span className="text-destructive">*</span></Label>
                <Input id="consent-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="consent-lastname">Last Name <span className="text-destructive">*</span></Label>
                <Input id="consent-lastname" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="consent-email">Email <span className="text-destructive">*</span></Label>
              <Input id="consent-email" type="email" value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="consent-phone-country">Country Code <span className="text-destructive">*</span></Label>
                <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                  <SelectTrigger id="consent-phone-country">
                    <SelectValue placeholder="+1" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+1">+1 (US/CA)</SelectItem>
                    <SelectItem value="+44">+44 (UK)</SelectItem>
                    <SelectItem value="+91">+91 (IN)</SelectItem>
                    <SelectItem value="+86">+86 (CN)</SelectItem>
                    <SelectItem value="+81">+81 (JP)</SelectItem>
                    <SelectItem value="+49">+49 (DE)</SelectItem>
                    <SelectItem value="+33">+33 (FR)</SelectItem>
                    <SelectItem value="+61">+61 (AU)</SelectItem>
                    <SelectItem value="+39">+39 (IT)</SelectItem>
                    <SelectItem value="+34">+34 (ES)</SelectItem>
                    <SelectItem value="+31">+31 (NL)</SelectItem>
                    <SelectItem value="+41">+41 (CH)</SelectItem>
                    <SelectItem value="+55">+55 (BR)</SelectItem>
                    <SelectItem value="+52">+52 (MX)</SelectItem>
                    <SelectItem value="+971">+971 (AE)</SelectItem>
                    <SelectItem value="+65">+65 (SG)</SelectItem>
                    <SelectItem value="+82">+82 (KR)</SelectItem>
                    <SelectItem value="+27">+27 (ZA)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="consent-phone-number">Phone Number <span className="text-destructive">*</span></Label>
                <Input
                  id="consent-phone-number"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>

            {/* WhatsApp Number */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="whatsapp-same"
                  checked={whatsAppSameAsPhone}
                  onChange={(e) => {
                    setWhatsAppSameAsPhone(e.target.checked);
                    if (e.target.checked) {
                      setWhatsAppCountryCode(phoneCountryCode);
                      setWhatsAppNumber(phoneNumber);
                    }
                  }}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                />
                <Label htmlFor="whatsapp-same" className="text-sm font-medium cursor-pointer">
                  Use same number for WhatsApp
                </Label>
              </div>
              {!whatsAppSameAsPhone && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pl-7">
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp-country-code">Country Code</Label>
                    <Select value={whatsAppCountryCode} onValueChange={setWhatsAppCountryCode}>
                      <SelectTrigger id="whatsapp-country-code">
                        <SelectValue placeholder="+1" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+1">+1 (US/CA)</SelectItem>
                        <SelectItem value="+44">+44 (UK)</SelectItem>
                        <SelectItem value="+91">+91 (IN)</SelectItem>
                        <SelectItem value="+86">+86 (CN)</SelectItem>
                        <SelectItem value="+81">+81 (JP)</SelectItem>
                        <SelectItem value="+49">+49 (DE)</SelectItem>
                        <SelectItem value="+33">+33 (FR)</SelectItem>
                        <SelectItem value="+61">+61 (AU)</SelectItem>
                        <SelectItem value="+39">+39 (IT)</SelectItem>
                        <SelectItem value="+34">+34 (ES)</SelectItem>
                        <SelectItem value="+31">+31 (NL)</SelectItem>
                        <SelectItem value="+41">+41 (CH)</SelectItem>
                        <SelectItem value="+55">+55 (BR)</SelectItem>
                        <SelectItem value="+52">+52 (MX)</SelectItem>
                        <SelectItem value="+971">+971 (AE)</SelectItem>
                        <SelectItem value="+65">+65 (SG)</SelectItem>
                        <SelectItem value="+82">+82 (KR)</SelectItem>
                        <SelectItem value="+27">+27 (ZA)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="whatsapp-number">WhatsApp Number</Label>
                    <Input
                      id="whatsapp-number"
                      type="tel"
                      value={whatsAppNumber}
                      onChange={(e) => setWhatsAppNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter WhatsApp number"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="consent-dob">Date of Birth</Label>
                <Input id="consent-dob" type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="consent-gender">Gender</Label>
                <select
                  id="consent-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="NON_BINARY">Non-binary</option>
                  <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Consent Sections */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Terms Acceptance */}
          <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl">Terms of Service</CardTitle>
              </div>
              <CardDescription>
                Please read and accept our terms of service to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms"
                  checked={formData.termsAccepted}
                  onCheckedChange={(checked) => handleCheckboxChange('termsAccepted', checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="terms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    I accept the Terms of Service
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Version {formData.termsVersion} •{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-foreground"
                    >
                      Read terms
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & GDPR */}
          <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl">Privacy & Data Protection</CardTitle>
              </div>
              <CardDescription>
                Your privacy matters. Review our data handling practices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="privacy"
                  checked={formData.privacyPolicyAccepted}
                  onCheckedChange={(checked) => handleCheckboxChange('privacyPolicyAccepted', checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="privacy"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I accept the Privacy Policy
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-foreground"
                    >
                      Read privacy policy
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="dataProcessing"
                  checked={formData.dataProcessingConsent}
                  onCheckedChange={(checked) => handleCheckboxChange('dataProcessingConsent', checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="dataProcessing"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I consent to data processing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Required for personalized services
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="marketing"
                  checked={formData.marketingConsent}
                  onCheckedChange={(checked) => handleCheckboxChange('marketingConsent', checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="marketing"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I agree to receive marketing communications
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Optional • You can change this later
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="thirdParty"
                  checked={formData.thirdPartyDataSharingConsent}
                  onCheckedChange={(checked) => handleCheckboxChange('thirdPartyDataSharingConsent', checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="thirdParty"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I consent to third-party data sharing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Optional • For enhanced features
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User Consent */}
          <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl">User Consent</CardTitle>
              </div>
              <CardDescription>
                Manage how we use your information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                By continuing, you acknowledge that you have read and understood our data handling practices.
              </p>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span>Your data is encrypted and secure</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span>You can withdraw consent at any time</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span>GDPR compliant data handling</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Settings className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl">Notification Preferences</CardTitle>
              </div>
              <CardDescription>
                Choose how you'd like to be notified
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="emailNotifications"
                  checked={formData.emailNotifications}
                  onCheckedChange={(checked) => handleCheckboxChange('emailNotifications', checked as boolean)}
                />
                <Label htmlFor="emailNotifications" className="cursor-pointer">
                  Email notifications
                </Label>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="pushNotifications"
                  checked={formData.pushNotifications}
                  onCheckedChange={(checked) => handleCheckboxChange('pushNotifications', checked as boolean)}
                />
                <Label htmlFor="pushNotifications" className="cursor-pointer">
                  Push notifications
                </Label>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="smsNotifications"
                  checked={formData.smsNotifications}
                  onCheckedChange={(checked) => handleCheckboxChange('smsNotifications', checked as boolean)}
                />
                <Label htmlFor="smsNotifications" className="cursor-pointer">
                  SMS notifications
                </Label>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="inAppNotifications"
                  checked={formData.inAppNotifications}
                  onCheckedChange={(checked) => handleCheckboxChange('inAppNotifications', checked as boolean)}
                />
                <Label htmlFor="inAppNotifications" className="cursor-pointer">
                  In-app notifications
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Complete Button */}
        <div className="flex justify-center pt-6 animate-fade-in-up">
          <Button
            variant="primary"
            size="lg"
            onClick={handleComplete}
            disabled={!allRequiredAccepted || isSubmitting}
            className="min-w-[200px] h-12 text-base font-semibold"
          >
            {isSubmitting ? 'Saving...' : 'Complete Setup →'}
          </Button>
        </div>

        {!allRequiredAccepted && (
          <p className="text-center text-sm text-muted-foreground">
            Please accept all required terms and consents to continue
          </p>
        )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OnboardingConsentPage;
